import React, { useState, useEffect, useCallback } from 'react';
import {
  Button, ButtonGroup, Form, Modal,
  Spinner, Table
} from 'react-bootstrap';
import {
  FaPlus, FaSync, FaBuilding, FaSearch,
  FaBan, FaCheckCircle, FaTimesCircle, FaEye, FaEdit
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../services/api';

// ==========================================
// SKELETON
// ==========================================
const SkeletonRow = () => (
  <tr>
    {[1, 2, 3, 4, 5, 6, 7].map(i => (
      <td key={i}>
        <div className="placeholder-glow">
          <span className="placeholder col-8" style={{ height: '16px', borderRadius: '4px' }} />
        </div>
      </td>
    ))}
  </tr>
);

// ==========================================
// BADGES
// ==========================================
const StatusBadge = ({ status }) => {
  const map = {
    ACTIVE:    { label: 'Ativa',     bg: '#198754' },
    SUSPENDED: { label: 'Suspensa',  bg: '#ffc107' },
    CANCELED:  { label: 'Cancelada', bg: '#dc3545' },
  };
  const s = map[status] || { label: status, bg: '#6c757d' };
  return (
    <span style={{ backgroundColor: s.bg, color: '#fff', padding: '4px 8px', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold' }}>
      {s.label}
    </span>
  );
};

const PlanBadge = ({ tier }) => {
  const map = {
    BASIC:      { label: 'Basic',      bg: '#6c757d' },
    PRO:        { label: 'Pro',        bg: '#0d6efd' },
    ENTERPRISE: { label: 'Enterprise', bg: '#212529' },
  };
  const p = map[tier] || { label: tier || '—', bg: '#6c757d' };
  return (
    <span style={{ backgroundColor: p.bg, color: '#fff', padding: '4px 8px', borderRadius: '8px', fontSize: '12px', fontWeight: '600' }}>
      {p.label}
    </span>
  );
};

// ==========================================
// INFO ROW
// ==========================================
const InfoRow = ({ label, value }) => (
  <div className="d-flex justify-content-between align-items-center border-bottom py-2">
    <small className="text-muted">{label}</small>
    <span style={{ fontSize: '14px' }}>{value}</span>
  </div>
);

// ==========================================
// MODAL: CRIAR EMPRESA
// ==========================================
const ModalCriarEmpresa = ({ show, onClose, onSuccess }) => {
  const initialState = {
    name: '', corporateName: '', cnpj: '', email: '', phone: '',
    municipalRegistration: '', stateRegistration: '', taxRegime: '',
    zipCode: '', street: '', number: '', complement: '',
    neighborhood: '', city: '', state: '', ibgeCode: '',
    userName: '', userEmail: '', password: '',
    planId: '',
    // ✅ Cobrança — valores padrão já preenchidos
    overduePaymentDays: 30,
    mulctEnabled:       true,
    mulctType:          'percentage',
    mulctValue:         2,
    interestEnabled:    true,
    interestType:       'percentage',
    interestValue:      1,
  };

  const [form, setForm]             = useState(initialState);
  const [errors, setErrors]         = useState({});
  const [loading, setLoading]       = useState(false);
  const [loadingCep, setLoadingCep] = useState(false);
  const [planos, setPlanos]         = useState([]);
  const [abaAtiva, setAbaAtiva]     = useState('empresa');

  useEffect(() => {
    if (!show) { setForm(initialState); setErrors({}); setAbaAtiva('empresa'); return; }
    api.get('/superadmin/plans')
      .then(res => setPlanos(res.data))
      .catch(() => toast.error('Erro ao carregar planos'));
  }, [show]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const buscarCep = async () => {
    const cep = form.zipCode.replace(/\D/g, '');
    if (cep.length !== 8) return;
    try {
      setLoadingCep(true);
      const res  = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await res.json();
      if (data.erro) { toast.warning('CEP não encontrado'); return; }
      setForm(prev => ({
        ...prev,
        street: data.logradouro || '', neighborhood: data.bairro || '',
        city: data.localidade || '', state: data.uf || '', ibgeCode: data.ibge || '',
      }));
    } catch { toast.error('Erro ao buscar CEP'); }
    finally { setLoadingCep(false); }
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim())        errs.name      = 'Obrigatório';
    if (!form.cnpj.trim())        errs.cnpj      = 'Obrigatório';
    if (!form.email.trim())       errs.email     = 'Obrigatório';
    if (!form.userName.trim())    errs.userName  = 'Obrigatório';
    if (!form.userEmail.trim())   errs.userEmail = 'Obrigatório';
    if (form.password.length < 8) errs.password  = 'Mínimo 8 caracteres';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      const camposEmpresa = ['name', 'cnpj', 'email', 'phone', 'corporateName'];
      setAbaAtiva(Object.keys(errs).some(k => camposEmpresa.includes(k)) ? 'empresa' : 'admin');
      return;
    }
    try {
      setLoading(true);
      await api.post('/companies', {
        name:                  form.name          || undefined,
        corporateName:         form.corporateName || undefined,
        cnpj:                  form.cnpj          || undefined,
        email:                 form.email         || undefined,
        phone:                 form.phone         || undefined,
        municipalRegistration: form.municipalRegistration || undefined,
        stateRegistration:     form.stateRegistration     || undefined,
        taxRegime:             form.taxRegime              || undefined,
        zipCode:               form.zipCode       || undefined,
        street:                form.street        || undefined,
        number:                form.number        || undefined,
        complement:            form.complement    || undefined,
        neighborhood:          form.neighborhood  || undefined,
        city:                  form.city          || undefined,
        state:                 form.state         || undefined,
        ibgeCode:              form.ibgeCode      || undefined,
        userName:              form.userName,
        userEmail:             form.userEmail,
        password:              form.password,
        planId:                form.planId        || undefined,
        // ✅ Cobrança enviada na criação
        overduePaymentDays: Number(form.overduePaymentDays),
        mulctEnabled:       form.mulctEnabled,
        mulctType:          form.mulctType,
        mulctValue:         Number(form.mulctValue),
        interestEnabled:    form.interestEnabled,
        interestType:       form.interestType,
        interestValue:      Number(form.interestValue),
      });
      toast.success('Empresa criada com sucesso!');
      onSuccess(); onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.response?.data?.error || 'Erro ao criar empresa');
    } finally { setLoading(false); }
  };

  const REGIMES = ['Simples Nacional', 'Simples Nacional – Excesso de sublimite', 'Lucro Presumido', 'Lucro Real'];

  // ✅ Aba cobrança adicionada
  const abas = [
    { key: 'empresa',  label: '🏢 Empresa'  },
    { key: 'fiscal',   label: '📄 Fiscal'   },
    { key: 'endereco', label: '📍 Endereço' },
    { key: 'admin',    label: '👤 Admin'    },
    { key: 'plano',    label: '📦 Plano'    },
    { key: 'cobranca', label: '⚙️ Cobrança' },
  ];

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title><FaBuilding className="me-2 text-primary" />Nova Empresa</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <div className="d-flex gap-1 mb-4 flex-wrap">
            {abas.map(aba => (
              <Button key={aba.key} size="sm" type="button"
                variant={abaAtiva === aba.key ? 'primary' : 'outline-secondary'}
                onClick={() => setAbaAtiva(aba.key)}>{aba.label}
              </Button>
            ))}
          </div>

          {/* ABA: EMPRESA */}
          {abaAtiva === 'empresa' && (
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <Form.Label className="fw-semibold">Nome Fantasia *</Form.Label>
                <Form.Control name="name" value={form.name} onChange={handleChange} placeholder="Ex: Maxtra Mídia" isInvalid={!!errors.name} />
                <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
              </div>
              <div className="col-12 col-md-6">
                <Form.Label className="fw-semibold">Razão Social</Form.Label>
                <Form.Control name="corporateName" value={form.corporateName} onChange={handleChange} placeholder="Ex: Maxtra Mídia LTDA" />
              </div>
              <div className="col-12 col-md-6">
                <Form.Label className="fw-semibold">CNPJ / CPF *</Form.Label>
                <Form.Control name="cnpj" value={form.cnpj} onChange={handleChange} placeholder="00.000.000/0001-00" isInvalid={!!errors.cnpj} />
                <Form.Control.Feedback type="invalid">{errors.cnpj}</Form.Control.Feedback>
              </div>
              <div className="col-12 col-md-6">
                <Form.Label className="fw-semibold">E-mail da Empresa *</Form.Label>
                <Form.Control name="email" type="email" value={form.email} onChange={handleChange} placeholder="contato@empresa.com" isInvalid={!!errors.email} />
                <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
              </div>
              <div className="col-12 col-md-6">
                <Form.Label className="fw-semibold">Telefone</Form.Label>
                <Form.Control name="phone" value={form.phone} onChange={handleChange} placeholder="(85) 99999-9999" />
              </div>
            </div>
          )}

          {/* ABA: FISCAL */}
          {abaAtiva === 'fiscal' && (
            <div className="row g-3">
              <div className="col-12">
                <div className="alert alert-info py-2" style={{ fontSize: '13px' }}>
                  💡 Dados necessários para emissão de <strong>NFS-e</strong> via Nuvem Fiscal.
                </div>
              </div>
              <div className="col-12 col-md-6">
                <Form.Label className="fw-semibold">Inscrição Municipal</Form.Label>
                <Form.Control name="municipalRegistration" value={form.municipalRegistration} onChange={handleChange} placeholder="Ex: 123456" />
              </div>
              <div className="col-12 col-md-6">
                <Form.Label className="fw-semibold">Inscrição Estadual</Form.Label>
                <Form.Control name="stateRegistration" value={form.stateRegistration} onChange={handleChange} placeholder="Ex: 12345678-9" />
              </div>
              <div className="col-12">
                <Form.Label className="fw-semibold">Regime Tributário</Form.Label>
                <Form.Select name="taxRegime" value={form.taxRegime} onChange={handleChange}>
                  <option value="">Selecione...</option>
                  {REGIMES.map(r => <option key={r} value={r}>{r}</option>)}
                </Form.Select>
              </div>
            </div>
          )}

          {/* ABA: ENDEREÇO */}
          {abaAtiva === 'endereco' && (
            <div className="row g-3">
              <div className="col-12 col-md-4">
                <Form.Label className="fw-semibold">CEP</Form.Label>
                <div className="input-group">
                  <Form.Control name="zipCode" value={form.zipCode} onChange={handleChange} placeholder="00000-000" onBlur={buscarCep} />
                  <Button variant="outline-secondary" type="button" onClick={buscarCep} disabled={loadingCep}>
                    {loadingCep ? <Spinner size="sm" /> : <FaSearch size={12} />}
                  </Button>
                </div>
                <Form.Text className="text-muted">Preenchimento automático</Form.Text>
              </div>
              <div className="col-12 col-md-8">
                <Form.Label className="fw-semibold">Logradouro</Form.Label>
                <Form.Control name="street" value={form.street} onChange={handleChange} placeholder="Ex: Rua das Flores" />
              </div>
              <div className="col-12 col-md-3">
                <Form.Label className="fw-semibold">Número</Form.Label>
                <Form.Control name="number" value={form.number} onChange={handleChange} placeholder="Ex: 123" />
              </div>
              <div className="col-12 col-md-4">
                <Form.Label className="fw-semibold">Complemento</Form.Label>
                <Form.Control name="complement" value={form.complement} onChange={handleChange} placeholder="Ex: Sala 02" />
              </div>
              <div className="col-12 col-md-5">
                <Form.Label className="fw-semibold">Bairro</Form.Label>
                <Form.Control name="neighborhood" value={form.neighborhood} onChange={handleChange} placeholder="Ex: Centro" />
              </div>
              <div className="col-12 col-md-6">
                <Form.Label className="fw-semibold">Cidade</Form.Label>
                <Form.Control name="city" value={form.city} onChange={handleChange} placeholder="Ex: Fortaleza" />
              </div>
              <div className="col-6 col-md-3">
                <Form.Label className="fw-semibold">UF</Form.Label>
                <Form.Control name="state" value={form.state} onChange={handleChange} placeholder="CE" maxLength={2} style={{ textTransform: 'uppercase' }} />
              </div>
              <div className="col-6 col-md-3">
                <Form.Label className="fw-semibold">Cód. IBGE</Form.Label>
                <Form.Control name="ibgeCode" value={form.ibgeCode} onChange={handleChange} placeholder="2304400" />
                <Form.Text className="text-muted">Preenchido via CEP</Form.Text>
              </div>
            </div>
          )}

          {/* ABA: ADMIN */}
          {abaAtiva === 'admin' && (
            <div className="row g-3">
              <div className="col-12">
                <div className="alert alert-warning py-2" style={{ fontSize: '13px' }}>
                  👤 Este usuário terá acesso total como <strong>Administrador</strong> da empresa.
                </div>
              </div>
              <div className="col-12 col-md-6">
                <Form.Label className="fw-semibold">Nome *</Form.Label>
                <Form.Control name="userName" value={form.userName} onChange={handleChange} placeholder="Ex: João Silva" isInvalid={!!errors.userName} />
                <Form.Control.Feedback type="invalid">{errors.userName}</Form.Control.Feedback>
              </div>
              <div className="col-12 col-md-6">
                <Form.Label className="fw-semibold">E-mail de Acesso *</Form.Label>
                <Form.Control name="userEmail" type="email" value={form.userEmail} onChange={handleChange} placeholder="admin@empresa.com" isInvalid={!!errors.userEmail} />
                <Form.Control.Feedback type="invalid">{errors.userEmail}</Form.Control.Feedback>
              </div>
              <div className="col-12 col-md-6">
                <Form.Label className="fw-semibold">Senha Inicial *</Form.Label>
                <Form.Control name="password" type="password" value={form.password} onChange={handleChange} placeholder="Mínimo 8 caracteres" isInvalid={!!errors.password} />
                <Form.Control.Feedback type="invalid">{errors.password}</Form.Control.Feedback>
              </div>
            </div>
          )}

          {/* ABA: PLANO */}
          {abaAtiva === 'plano' && (
            <div className="row g-3">
              <div className="col-12">
                <div className="alert alert-info py-2" style={{ fontSize: '13px' }}>
                  📦 Se nenhum plano for selecionado, a empresa será criada no plano <strong>BASIC</strong> automaticamente.
                </div>
              </div>
              {planos.length === 0 ? (
                <div className="col-12 text-center py-4 text-muted">
                  <Spinner size="sm" className="me-2" />Carregando planos...
                </div>
              ) : (
                planos.map(plano => (
                  <div className="col-12 col-md-4" key={plano.id}>
                    <div
                      className={`card h-100 ${form.planId === plano.id ? 'border-primary border-2' : ''}`}
                      style={{ cursor: 'pointer', transition: 'border .2s' }}
                      onClick={() => setForm(prev => ({ ...prev, planId: plano.id }))}
                    >
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <PlanBadge tier={plano.tier} />
                          {form.planId === plano.id && <FaCheckCircle className="text-primary" />}
                        </div>
                        <h6 className="fw-bold mb-1">{plano.name}</h6>
                        <p className="text-primary fw-bold mb-2">
                          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(plano.price / 100)}
                          <small className="text-muted fw-normal">/mês</small>
                        </p>
                        {/* <ul className="list-unstyled mb-0" style={{ fontSize: '12px' }}>
                          <li className="text-muted">👥 {plano.userLimit} usuários</li>
                          <li className="text-muted">🧑‍💼 {plano.customerLimit} clientes</li>
                          <li className={plano.allowsDigitalContracts ? 'text-success' : 'text-muted'}>
                            {plano.allowsDigitalContracts ? '✅' : '❌'} Contratos digitais
                          </li>
                          <li className={plano.allowsApiIntegration ? 'text-success' : 'text-muted'}>
                            {plano.allowsApiIntegration ? '✅' : '❌'} Integração API
                          </li>
                        </ul> */}
                      </div>
                    </div>
                  </div>
                ))
              )}
              {form.planId && (
                <div className="col-12">
                  <Button variant="link" size="sm" className="text-muted p-0" type="button"
                    onClick={() => setForm(prev => ({ ...prev, planId: '' }))}>
                    ✕ Remover seleção (usar BASIC padrão)
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* ✅ ABA: COBRANÇA */}
          {abaAtiva === 'cobranca' && (
            <div className="row g-3">
              <div className="col-12">
                <div className="alert alert-info py-2" style={{ fontSize: '13px' }}>
                  ⚙️ Valores padrão já configurados. Ajuste conforme necessário antes de criar a empresa.
                </div>
              </div>

              <div className="col-12 col-md-4">
                <Form.Label className="fw-semibold">Expira após vencimento (dias)</Form.Label>
                <Form.Control
                  type="number" min={1} max={100}
                  name="overduePaymentDays"
                  value={form.overduePaymentDays}
                  onChange={handleChange}
                />
                <Form.Text className="text-muted">Fatura expira X dias após o vencimento</Form.Text>
              </div>

              {/* ── MULTA ── */}
              <div className="col-12">
                <hr className="my-1" />
                <small className="fw-bold text-muted text-uppercase">Multa</small>
              </div>

              <div className="col-12 col-md-4 d-flex align-items-center">
                <Form.Check
                  type="switch"
                  id="criar-mulctEnabled"
                  label="Multa habilitada"
                  checked={form.mulctEnabled}
                  onChange={e => setForm(prev => ({ ...prev, mulctEnabled: e.target.checked }))}
                />
              </div>

              <div className="col-12 col-md-4">
                <Form.Label className="fw-semibold">Tipo</Form.Label>
                <Form.Select
                  name="mulctType"
                  value={form.mulctType}
                  onChange={handleChange}
                  disabled={!form.mulctEnabled}
                >
                  <option value="percentage">Percentual (%)</option>
                  <option value="fixedValue">Valor Fixo (R$)</option>
                </Form.Select>
              </div>

              <div className="col-12 col-md-4">
                <Form.Label className="fw-semibold">
                  Valor {form.mulctType === 'percentage' ? '(%)' : '(R$)'}
                </Form.Label>
                <Form.Control
                  type="number" min={0} step={0.1}
                  name="mulctValue"
                  value={form.mulctValue}
                  onChange={handleChange}
                  disabled={!form.mulctEnabled}
                  placeholder={form.mulctType === 'percentage' ? 'Ex: 2' : 'Ex: 5.00'}
                />
                <Form.Text className="text-muted">
                  {form.mulctEnabled
                    ? form.mulctType === 'percentage'
                      ? `${form.mulctValue}% sobre o valor da fatura`
                      : `R$ ${Number(form.mulctValue).toFixed(2)} fixo`
                    : 'Multa desabilitada'}
                </Form.Text>
              </div>

              {/* ── JUROS ── */}
              <div className="col-12">
                <hr className="my-1" />
                <small className="fw-bold text-muted text-uppercase">Juros ao mês</small>
              </div>

              <div className="col-12 col-md-4 d-flex align-items-center">
                <Form.Check
                  type="switch"
                  id="criar-interestEnabled"
                  label="Juros habilitados"
                  checked={form.interestEnabled}
                  onChange={e => setForm(prev => ({ ...prev, interestEnabled: e.target.checked }))}
                />
              </div>

              <div className="col-12 col-md-4">
                <Form.Label className="fw-semibold">Tipo</Form.Label>
                <Form.Select
                  name="interestType"
                  value={form.interestType}
                  onChange={handleChange}
                  disabled={!form.interestEnabled}
                >
                  <option value="percentage">Percentual (%)</option>
                  <option value="fixedValue">Valor Fixo (R$)</option>
                </Form.Select>
              </div>

              <div className="col-12 col-md-4">
                <Form.Label className="fw-semibold">
                  Valor {form.interestType === 'percentage' ? '(% ao mês)' : '(R$/mês)'}
                </Form.Label>
                <Form.Control
                  type="number" min={0} step={0.1}
                  name="interestValue"
                  value={form.interestValue}
                  onChange={handleChange}
                  disabled={!form.interestEnabled}
                  placeholder={form.interestType === 'percentage' ? 'Ex: 1' : 'Ex: 2.00'}
                />
                <Form.Text className="text-muted">
                  {form.interestEnabled
                    ? form.interestType === 'percentage'
                      ? `${form.interestValue}% ao mês`
                      : `R$ ${Number(form.interestValue).toFixed(2)} por mês`
                    : 'Juros desabilitados'}
                </Form.Text>
              </div>
            </div>
          )}

        </Modal.Body>
        <Modal.Footer className="justify-content-between">
          <small className="text-muted">* Campos obrigatórios</small>
          <div className="d-flex gap-2">
            <Button variant="outline-dark" onClick={onClose} disabled={loading} type="button">Cancelar</Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? <><Spinner size="sm" className="me-2" />Criando...</> : <><FaPlus className="me-2" />Criar Empresa</>}
            </Button>
          </div>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};


// ==========================================
// MODAL: EDITAR EMPRESA  ✅ COM ABA COBRANÇA
// ==========================================
const ModalEditarEmpresa = ({ show, onClose, onSuccess, empresa }) => {
  const initialState = {
    name: '', corporateName: '', cnpj: '', email: '', phone: '',
    municipalRegistration: '', stateRegistration: '', taxRegime: '',
    zipCode: '', street: '', number: '', complement: '',
    neighborhood: '', city: '', state: '', ibgeCode: '',
    planId: '',
    // ✅ Cobrança
    overduePaymentDays: 30,
    mulctEnabled:       true,
    mulctType:          'percentage',
    mulctValue:         2,
    interestEnabled:    true,
    interestType:       'percentage',
    interestValue:      1,
  };

  const [form, setForm]             = useState(initialState);
  const [errors, setErrors]         = useState({});
  const [loading, setLoading]       = useState(false);
  const [loadingCep, setLoadingCep] = useState(false);
  const [abaAtiva, setAbaAtiva]     = useState('empresa');
  const [planos, setPlanos]         = useState([]);

  const planoOriginalId = empresa?.subscriptions?.[0]?.plan?.id || '';
  const planoMudou      = form.planId && form.planId !== planoOriginalId;

  useEffect(() => {
    if (!show || !empresa) return;
    setAbaAtiva('empresa');
    setErrors({});

    api.get('/superadmin/plans')
      .then(res => setPlanos(res.data))
      .catch(() => toast.error('Erro ao carregar planos'));

    const sub = empresa.subscriptions?.[0];

    setForm({
      name:                  empresa.name                  || '',
      corporateName:         empresa.corporateName         || '',
      cnpj:                  empresa.cnpj                  || '',
      email:                 empresa.email                 || '',
      phone:                 empresa.phone                 || '',
      municipalRegistration: empresa.municipalRegistration || '',
      stateRegistration:     empresa.stateRegistration     || '',
      taxRegime:             empresa.taxRegime             || '',
      zipCode:               empresa.zipCode               || '',
      street:                empresa.street                || '',
      number:                empresa.number                || '',
      complement:            empresa.complement            || '',
      neighborhood:          empresa.neighborhood          || '',
      city:                  empresa.city                  || '',
      state:                 empresa.state                 || '',
      ibgeCode:              empresa.ibgeCode              || '',
      planId:                sub?.plan?.id                 || '',
      // ✅ Cobrança — lê da subscription
      overduePaymentDays: sub?.overduePaymentDays ?? 30,
      mulctEnabled:       sub?.mulctEnabled       ?? true,
      mulctType:          sub?.mulctType          ?? 'percentage',
      mulctValue:         sub?.mulctValue         ?? 2,
      interestEnabled:    sub?.interestEnabled    ?? true,
      interestType:       sub?.interestType       ?? 'percentage',
      interestValue:      sub?.interestValue      ?? 1,
    });
  }, [show, empresa]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const buscarCep = async () => {
    const cep = form.zipCode.replace(/\D/g, '');
    if (cep.length !== 8) return;
    try {
      setLoadingCep(true);
      const res  = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await res.json();
      if (data.erro) { toast.warning('CEP não encontrado'); return; }
      setForm(prev => ({
        ...prev,
        street: data.logradouro || '', neighborhood: data.bairro || '',
        city: data.localidade || '', state: data.uf || '', ibgeCode: data.ibge || '',
      }));
    } catch { toast.error('Erro ao buscar CEP'); }
    finally { setLoadingCep(false); }
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim())  errs.name  = 'Obrigatório';
    if (!form.email.trim()) errs.email = 'Obrigatório';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); setAbaAtiva('empresa'); return; }
    try {
      setLoading(true);
      await api.put(`/superadmin/companies/${empresa.id}`, {
        name:                  form.name                  || undefined,
        corporateName:         form.corporateName         || undefined,
        cnpj:                  form.cnpj                  || undefined,
        email:                 form.email                 || undefined,
        phone:                 form.phone                 || undefined,
        municipalRegistration: form.municipalRegistration || undefined,
        stateRegistration:     form.stateRegistration     || undefined,
        taxRegime:             form.taxRegime             || undefined,
        zipCode:               form.zipCode               || undefined,
        street:                form.street                || undefined,
        number:                form.number                || undefined,
        complement:            form.complement            || undefined,
        neighborhood:          form.neighborhood          || undefined,
        city:                  form.city                  || undefined,
        state:                 form.state                 || undefined,
        ibgeCode:              form.ibgeCode              || undefined,
        planId:                planoMudou ? form.planId : undefined,
        // ✅ Cobrança
        overduePaymentDays: Number(form.overduePaymentDays),
        mulctEnabled:       form.mulctEnabled,
        mulctType:          form.mulctType,
        mulctValue:         Number(form.mulctValue),
        interestEnabled:    form.interestEnabled,
        interestType:       form.interestType,
        interestValue:      Number(form.interestValue),
      });
      toast.success(planoMudou ? 'Empresa atualizada e plano alterado!' : 'Empresa atualizada!');
      onSuccess(); onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.response?.data?.error || 'Erro ao atualizar empresa');
    } finally { setLoading(false); }
  };

  const REGIMES = ['Simples Nacional', 'Simples Nacional – Excesso de sublimite', 'Lucro Presumido', 'Lucro Real'];

  const abas = [
    { key: 'empresa',  label: '🏢 Empresa'  },
    { key: 'fiscal',   label: '📄 Fiscal'   },
    { key: 'endereco', label: '📍 Endereço' },
    { key: 'plano',    label: '📦 Plano'    },
    { key: 'cobranca', label: '⚙️ Cobrança' }, // ✅
  ];

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title><FaEdit className="me-2 text-warning" />Editar Empresa</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>

          {/* ABAS */}
          <div className="d-flex gap-1 mb-4 flex-wrap">
            {abas.map(aba => (
              <Button key={aba.key} size="sm" type="button"
                variant={abaAtiva === aba.key ? 'primary' : 'outline-secondary'}
                onClick={() => setAbaAtiva(aba.key)}>
                {aba.label}
                {aba.key === 'plano' && planoMudou && (
                  <span className="ms-1 badge bg-warning text-dark" style={{ fontSize: '10px' }}>alterado</span>
                )}
              </Button>
            ))}
          </div>

          {/* ABA: EMPRESA */}
          {abaAtiva === 'empresa' && (
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <Form.Label className="fw-semibold">Nome Fantasia *</Form.Label>
                <Form.Control name="name" value={form.name} onChange={handleChange} placeholder="Ex: Maxtra Mídia" isInvalid={!!errors.name} />
                <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
              </div>
              <div className="col-12 col-md-6">
                <Form.Label className="fw-semibold">Razão Social</Form.Label>
                <Form.Control name="corporateName" value={form.corporateName} onChange={handleChange} placeholder="Ex: Maxtra Mídia LTDA" />
              </div>
              <div className="col-12 col-md-6">
                <Form.Label className="fw-semibold">CNPJ / CPF</Form.Label>
                <Form.Control name="cnpj" value={form.cnpj} onChange={handleChange} placeholder="00.000.000/0001-00" />
              </div>
              <div className="col-12 col-md-6">
                <Form.Label className="fw-semibold">E-mail *</Form.Label>
                <Form.Control name="email" type="email" value={form.email} onChange={handleChange} placeholder="contato@empresa.com" isInvalid={!!errors.email} />
                <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
              </div>
              <div className="col-12 col-md-6">
                <Form.Label className="fw-semibold">Telefone</Form.Label>
                <Form.Control name="phone" value={form.phone} onChange={handleChange} placeholder="(85) 99999-9999" />
              </div>
            </div>
          )}

          {/* ABA: FISCAL */}
          {abaAtiva === 'fiscal' && (
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <Form.Label className="fw-semibold">Inscrição Municipal</Form.Label>
                <Form.Control name="municipalRegistration" value={form.municipalRegistration} onChange={handleChange} placeholder="Ex: 123456" />
              </div>
              <div className="col-12 col-md-6">
                <Form.Label className="fw-semibold">Inscrição Estadual</Form.Label>
                <Form.Control name="stateRegistration" value={form.stateRegistration} onChange={handleChange} placeholder="Ex: 12345678-9" />
              </div>
              <div className="col-12">
                <Form.Label className="fw-semibold">Regime Tributário</Form.Label>
                <Form.Select name="taxRegime" value={form.taxRegime} onChange={handleChange}>
                  <option value="">Selecione...</option>
                  {REGIMES.map(r => <option key={r} value={r}>{r}</option>)}
                </Form.Select>
              </div>
            </div>
          )}

          {/* ABA: ENDEREÇO */}
          {abaAtiva === 'endereco' && (
            <div className="row g-3">
              <div className="col-12 col-md-4">
                <Form.Label className="fw-semibold">CEP</Form.Label>
                <div className="input-group">
                  <Form.Control name="zipCode" value={form.zipCode} onChange={handleChange} placeholder="00000-000" onBlur={buscarCep} />
                  <Button variant="outline-secondary" type="button" onClick={buscarCep} disabled={loadingCep}>
                    {loadingCep ? <Spinner size="sm" /> : <FaSearch size={12} />}
                  </Button>
                </div>
                <Form.Text className="text-muted">Preenchimento automático</Form.Text>
              </div>
              <div className="col-12 col-md-8">
                <Form.Label className="fw-semibold">Logradouro</Form.Label>
                <Form.Control name="street" value={form.street} onChange={handleChange} placeholder="Ex: Rua das Flores" />
              </div>
              <div className="col-12 col-md-3">
                <Form.Label className="fw-semibold">Número</Form.Label>
                <Form.Control name="number" value={form.number} onChange={handleChange} placeholder="Ex: 123" />
              </div>
              <div className="col-12 col-md-4">
                <Form.Label className="fw-semibold">Complemento</Form.Label>
                <Form.Control name="complement" value={form.complement} onChange={handleChange} placeholder="Ex: Sala 02" />
              </div>
              <div className="col-12 col-md-5">
                <Form.Label className="fw-semibold">Bairro</Form.Label>
                <Form.Control name="neighborhood" value={form.neighborhood} onChange={handleChange} placeholder="Ex: Centro" />
              </div>
              <div className="col-12 col-md-6">
                <Form.Label className="fw-semibold">Cidade</Form.Label>
                <Form.Control name="city" value={form.city} onChange={handleChange} placeholder="Ex: Fortaleza" />
              </div>
              <div className="col-6 col-md-3">
                <Form.Label className="fw-semibold">UF</Form.Label>
                <Form.Control name="state" value={form.state} onChange={handleChange} placeholder="CE" maxLength={2} style={{ textTransform: 'uppercase' }} />
              </div>
              <div className="col-6 col-md-3">
                <Form.Label className="fw-semibold">Cód. IBGE</Form.Label>
                <Form.Control name="ibgeCode" value={form.ibgeCode} onChange={handleChange} placeholder="2304400" />
                <Form.Text className="text-muted">Preenchido via CEP</Form.Text>
              </div>
            </div>
          )}

          {/* ABA: PLANO */}
          {abaAtiva === 'plano' && (
            <div className="row g-3">
              <div className="col-12">
                {planoMudou ? (
                  <div className="alert alert-warning py-2" style={{ fontSize: '13px' }}>
                    ⚠️ O plano será alterado. O <strong>valor da assinatura será atualizado</strong> na Lytex automaticamente.
                  </div>
                ) : (
                  <div className="alert alert-info py-2" style={{ fontSize: '13px' }}>
                    📦 Selecione um novo plano. O valor da cobrança será atualizado automaticamente.
                  </div>
                )}
              </div>
              {planos.length === 0 ? (
                <div className="col-12 text-center py-4 text-muted">
                  <Spinner size="sm" className="me-2" />Carregando planos...
                </div>
              ) : (
                planos.map(plano => {
                  const isAtual       = plano.id === planoOriginalId;
                  const isSelecionado = form.planId === plano.id;
                  return (
                    <div className="col-12 col-md-4" key={plano.id}>
                      <div
                        className={`card h-100 ${isSelecionado ? 'border-primary border-2' : ''}`}
                        style={{ cursor: 'pointer', transition: 'border .2s', position: 'relative' }}
                        onClick={() => setForm(prev => ({ ...prev, planId: plano.id }))}
                      >
                        {isAtual && (
                          <div style={{
                            position: 'absolute', top: 8, right: 8,
                            backgroundColor: '#198754', color: '#fff',
                            fontSize: '10px', fontWeight: 700,
                            padding: '2px 7px', borderRadius: '6px',
                          }}>atual</div>
                        )}
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <PlanBadge tier={plano.tier} />
                            {isSelecionado && <FaCheckCircle className="text-primary" />}
                          </div>
                          <h6 className="fw-bold mb-1">{plano.name}</h6>
                          <p className="text-primary fw-bold mb-2">
                            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(plano.price / 100)}
                            <small className="text-muted fw-normal">/mês</small>
                          </p>
                          <ul className="list-unstyled mb-0" style={{ fontSize: '12px' }}>
                            <li className="text-muted">👥 {plano.userLimit} usuários</li>
                            <li className="text-muted">🧑‍💼 {plano.customerLimit} clientes</li>
                            <li className={plano.allowsDigitalContracts ? 'text-success' : 'text-muted'}>
                              {plano.allowsDigitalContracts ? '✅' : '❌'} Contratos digitais
                            </li>
                            <li className={plano.allowsApiIntegration ? 'text-success' : 'text-muted'}>
                              {plano.allowsApiIntegration ? '✅' : '❌'} Integração API
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* ✅ ABA: COBRANÇA */}
          {abaAtiva === 'cobranca' && (
            <div className="row g-3">
              <div className="col-12">
                <div className="alert alert-info py-2" style={{ fontSize: '13px' }}>
                  ⚙️ Configurações aplicadas na assinatura da empresa na <strong>Lytex</strong>.
                  Ao salvar, a assinatura será atualizada automaticamente.
                </div>
              </div>

              {/* Dias para expirar */}
              <div className="col-12 col-md-4">
                <Form.Label className="fw-semibold">Expira após vencimento (dias)</Form.Label>
                <Form.Control
                  type="number" min={1} max={365}
                  name="overduePaymentDays"
                  value={form.overduePaymentDays}
                  onChange={handleChange}
                />
                <Form.Text className="text-muted">Fatura expira X dias após o vencimento</Form.Text>
              </div>

              {/* ── MULTA ── */}
              <div className="col-12">
                <hr className="my-1" />
                <small className="fw-bold text-muted text-uppercase">Multa</small>
              </div>

              <div className="col-12 col-md-4 d-flex align-items-center">
                <Form.Check
                  type="switch"
                  id="mulctEnabled"
                  label="Multa habilitada"
                  checked={form.mulctEnabled}
                  onChange={e => setForm(prev => ({ ...prev, mulctEnabled: e.target.checked }))}
                />
              </div>

              <div className="col-12 col-md-4">
                <Form.Label className="fw-semibold">Tipo</Form.Label>
                <Form.Select
                  name="mulctType"
                  value={form.mulctType}
                  onChange={handleChange}
                  disabled={!form.mulctEnabled}
                >
                  <option value="percentage">Percentual (%)</option>
                  <option value="fixedValue">Valor Fixo (R$)</option>
                </Form.Select>
              </div>

              <div className="col-12 col-md-4">
                <Form.Label className="fw-semibold">
                  Valor {form.mulctType === 'percentage' ? '(%)' : '(R$)'}
                </Form.Label>
                <Form.Control
                  type="number" min={0} step={0.1}
                  name="mulctValue"
                  value={form.mulctValue}
                  onChange={handleChange}
                  disabled={!form.mulctEnabled}
                  placeholder={form.mulctType === 'percentage' ? 'Ex: 2' : 'Ex: 5.00'}
                />
                <Form.Text className="text-muted">
                  {form.mulctEnabled
                    ? form.mulctType === 'percentage'
                      ? `${form.mulctValue}% sobre o valor da fatura`
                      : `R$ ${Number(form.mulctValue).toFixed(2)} fixo`
                    : 'Multa desabilitada'}
                </Form.Text>
              </div>

              {/* ── JUROS ── */}
              <div className="col-12">
                <hr className="my-1" />
                <small className="fw-bold text-muted text-uppercase">Juros ao mês</small>
              </div>

              <div className="col-12 col-md-4 d-flex align-items-center">
                <Form.Check
                  type="switch"
                  id="interestEnabled"
                  label="Juros habilitados"
                  checked={form.interestEnabled}
                  onChange={e => setForm(prev => ({ ...prev, interestEnabled: e.target.checked }))}
                />
              </div>

              <div className="col-12 col-md-4">
                <Form.Label className="fw-semibold">Tipo</Form.Label>
                <Form.Select
                  name="interestType"
                  value={form.interestType}
                  onChange={handleChange}
                  disabled={!form.interestEnabled}
                >
                  <option value="percentage">Percentual (%)</option>
                  <option value="fixedValue">Valor Fixo (R$)</option>
                </Form.Select>
              </div>

              <div className="col-12 col-md-4">
                <Form.Label className="fw-semibold">
                  Valor {form.interestType === 'percentage' ? '(% ao mês)' : '(R$/mês)'}
                </Form.Label>
                <Form.Control
                  type="number" min={0} step={0.1}
                  name="interestValue"
                  value={form.interestValue}
                  onChange={handleChange}
                  disabled={!form.interestEnabled}
                  placeholder={form.interestType === 'percentage' ? 'Ex: 1' : 'Ex: 2.00'}
                />
                <Form.Text className="text-muted">
                  {form.interestEnabled
                    ? form.interestType === 'percentage'
                      ? `${form.interestValue}% ao mês`
                      : `R$ ${Number(form.interestValue).toFixed(2)} por mês`
                    : 'Juros desabilitados'}
                </Form.Text>
              </div>

            </div>
          )}

        </Modal.Body>
        <Modal.Footer className="justify-content-between">
          <small className="text-muted">* Campos obrigatórios</small>
          <div className="d-flex gap-2">
            <Button variant="outline-dark" onClick={onClose} disabled={loading} type="button">Cancelar</Button>
            <Button variant="warning" type="submit" disabled={loading}>
              {loading ? <><Spinner size="sm" className="me-2" />Salvando...</> : <><FaEdit className="me-2" />Salvar Alterações</>}
            </Button>
          </div>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

// ==========================================
// MODAL: CONFIRMAR AÇÃO DE STATUS
// ==========================================
const ModalConfirmarStatus = ({ show, onClose, empresa, novoStatus, onConfirmar, loading }) => {
  const acoes = {
    ACTIVE:    { texto: 'ativar',    variant: 'success', icon: <FaCheckCircle className="me-2" /> },
    SUSPENDED: { texto: 'suspender', variant: 'warning', icon: <FaBan className="me-2" />         },
    CANCELED:  { texto: 'cancelar',  variant: 'danger',  icon: <FaTimesCircle className="me-2" /> },
  };
  const acao = acoes[novoStatus] || {};
  return (
    <Modal show={show} onHide={onClose} centered size="sm">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '16px' }}>Confirmar Ação</Modal.Title>
      </Modal.Header>
      <Modal.Body className="text-center py-4">
        <p className="mb-1">Deseja <strong>{acao.texto}</strong> a empresa:</p>
        <p className="fw-bold text-primary">{empresa?.name}</p>
      </Modal.Body>
      <Modal.Footer className="justify-content-center gap-2">
        <Button variant="outline-dark" onClick={onClose} disabled={loading}>Cancelar</Button>
        <Button variant={acao.variant} onClick={onConfirmar} disabled={loading}>
          {loading ? <Spinner size="sm" /> : <>{acao.icon} Confirmar</>}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// ==========================================
// MODAL: DETALHES DA EMPRESA
// ==========================================
const ModalDetalhes = ({ show, onClose, empresa, onUsuarioToggle }) => {
  const [loadingUserId, setLoadingUserId] = useState(null);

  if (!empresa) return null;

  const sub = empresa.subscriptions?.[0];

  const formatarData = (d) =>
    d ? new Date(d).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '—';

 const formatarMulta = (sub) => {
  if (!sub?.mulctEnabled) return <span className="text-muted">Desabilitada</span>;

  const valor = sub.mulctType === "percentage"
    ? `${sub.mulctValue}%`
    : new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(sub.mulctValue); // ✅ já é R$, não centavos

  return <span className="text-danger fw-semibold">{valor}</span>;
};

const formatarJuros = (sub) => {
  if (!sub?.interestEnabled) return <span className="text-muted">Desabilitados</span>;

  const valor = sub.interestType === "percentage"
    ? `${sub.interestValue}% ao mês`
    : new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(sub.interestValue); // ✅ já é R$, não centavos

  return <span className="text-warning fw-semibold">{valor}</span>;
};

  const toggleUsuario = async (usuario) => {
    try {
      setLoadingUserId(usuario.id);
      await api.patch(`/superadmin/users/${usuario.id}/toggle`);
      toast.success(`Usuário ${usuario.isActive ? 'desativado' : 'ativado'}!`);
      onUsuarioToggle();
    } catch {
      toast.error('Erro ao alterar status do usuário');
    } finally {
      setLoadingUserId(null);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title><FaBuilding className="me-2 text-primary" />{empresa.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row g-3">

          {/* ── Empresa ── */}
          <div className="col-12 col-md-6">
            <div className="card h-100">
              <div className="card-header py-2">
                <small className="fw-bold text-muted text-uppercase">Empresa</small>
              </div>
              <div className="card-body">
                <InfoRow label="Nome"         value={empresa.name} />
                <InfoRow label="Razão Social" value={empresa.corporateName || '—'} />
                <InfoRow label="CNPJ"         value={empresa.cnpj || '—'} />
                <InfoRow label="E-mail"       value={empresa.email} />
                <InfoRow label="Tel."         value={empresa.phone || '—'} />
                <InfoRow label="Cidade/UF"    value={empresa.city ? `${empresa.city}/${empresa.state}` : '—'} />
                <InfoRow label="Status"       value={<StatusBadge status={empresa.status} />} />
                <InfoRow label="Criada"       value={formatarData(empresa.createdAt)} />
              </div>
            </div>
          </div>

          {/* ── Assinatura ── */}
          <div className="col-12 col-md-6">
            <div className="card h-100">
              <div className="card-header py-2">
                <small className="fw-bold text-muted text-uppercase">Assinatura</small>
              </div>
              <div className="card-body">
                {sub ? (
                  <>
                    <InfoRow label="Plano"              value={<PlanBadge tier={sub.plan?.tier} />} />
                    <InfoRow label="Status"             value={sub.status} />
                    <InfoRow label="Início"             value={formatarData(sub.startDate)} />
                    <InfoRow label="Vencimento"         value={formatarData(sub.cycleEndDate)} />
                    <InfoRow
                      label="Valor"
                      value={
                        sub.plan?.price != null
                          ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(sub.plan.price / 100)
                          : '—'
                      }
                    />
                    <InfoRow label="Expira após venc."  value={sub.overduePaymentDays != null ? `${sub.overduePaymentDays} dias` : '—'} />
                    <InfoRow label="Multa"              value={formatarMulta(sub)} />
                    <InfoRow label="Juros"              value={formatarJuros(sub)} />
                  </>
                ) : (
                  <p className="text-muted text-center py-3">Sem assinatura ativa</p>
                )}
              </div>
            </div>
          </div>

          {/* ── Dados Fiscais ── */}
          {(empresa.municipalRegistration || empresa.stateRegistration || empresa.taxRegime) && (
            <div className="col-12">
              <div className="card">
                <div className="card-header py-2">
                  <small className="fw-bold text-muted text-uppercase">Dados Fiscais</small>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-4">
                      <InfoRow label="Insc. Municipal" value={empresa.municipalRegistration || '—'} />
                    </div>
                    <div className="col-md-4">
                      <InfoRow label="Insc. Estadual" value={empresa.stateRegistration || '—'} />
                    </div>
                    <div className="col-md-4">
                      <InfoRow label="Regime" value={empresa.taxRegime || '—'} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Usuários ── */}
          <div className="col-12">
            <div className="card">
              <div className="card-header py-2">
                <small className="fw-bold text-muted text-uppercase">
                  Usuários ({empresa.users?.length || 0})
                </small>
              </div>
              <div className="card-body p-0">
                <Table size="sm" className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Nome</th>
                      <th>E-mail</th>
                      <th>Perfil</th>
                      <th className="text-center">Status</th>
                      <th className="text-center">Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {empresa.users?.length > 0 ? (
                      empresa.users.map(u => (
                        <tr key={u.id}>
                          <td>{u.name}</td>
                          <td><small>{u.email}</small></td>
                          <td>
                            <span style={{ backgroundColor: u.role === 'ADMIN' ? '#0d6efd' : '#6c757d', color: '#fff', padding: '4px 8px', borderRadius: '8px', fontSize: '12px', fontWeight: 600 }}>
                              {u.role}
                            </span>
                          </td>
                          <td className="text-center">
                            <span style={{ backgroundColor: u.isActive ? '#198754' : '#6c757d', color: '#fff', padding: '4px 8px', borderRadius: '8px', fontSize: '12px', fontWeight: 600 }}>
                              {u.isActive ? 'Ativo' : 'Inativo'}
                            </span>
                          </td>
                          <td className="text-center">
                            <Button size="sm" variant={u.isActive ? 'outline-danger' : 'outline-success'}
                              onClick={() => toggleUsuario(u)} disabled={loadingUserId === u.id}
                              title={u.isActive ? 'Desativar usuário' : 'Ativar usuário'}>
                              {loadingUserId === u.id ? <Spinner size="sm" /> : u.isActive ? <FaTimesCircle /> : <FaCheckCircle />}
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="text-center text-muted py-3">Nenhum usuário</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </div>
          </div>

        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-dark" onClick={onClose}>Fechar</Button>
      </Modal.Footer>
    </Modal>
  );
};

// ==========================================
// COMPONENTE PRINCIPAL
// ==========================================
const Empresas = () => {
  const [empresas, setEmpresas]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [filtroStatus, setFiltroStatus] = useState('ALL');
  const [page, setPage]                 = useState(1);
  const [meta, setMeta]                 = useState({ total: 0, totalPages: 1 });
  const LIMIT = 15;

  const [showCriar, setShowCriar]             = useState(false);
  const [showEditar, setShowEditar]           = useState(false);
  const [empresaEditar, setEmpresaEditar]     = useState(null);
  const [showDetalhes, setShowDetalhes]       = useState(false);
  const [empresaDetalhes, setEmpresaDetalhes] = useState(null);
  const [showConfirmar, setShowConfirmar]     = useState(false);
  const [acaoStatus, setAcaoStatus]           = useState({ empresa: null, novoStatus: null });
  const [loadingStatus, setLoadingStatus]     = useState(false);

  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => { setSearch(searchInput); setPage(1); }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchEmpresas = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/superadmin/companies', {
        params: {
          search: search || undefined,
          status: filtroStatus !== 'ALL' ? filtroStatus : undefined,
          page,
          limit: LIMIT,
        }
      });
      setEmpresas(response.data.data);
      setMeta(response.data.meta);
    } catch {
      toast.error('Erro ao carregar empresas');
    } finally {
      setLoading(false);
    }
  }, [search, filtroStatus, page]);

  useEffect(() => { fetchEmpresas(); }, [fetchEmpresas]);

  const abrirDetalhes = async (empresa) => {
    try {
      const res = await api.get(`/superadmin/companies/${empresa.id}`);
      setEmpresaDetalhes(res.data);
      setShowDetalhes(true);
    } catch { toast.error('Erro ao carregar detalhes'); }
  };

  const recarregarDetalhes = async () => {
    if (!empresaDetalhes) return;
    try {
      const res = await api.get(`/superadmin/companies/${empresaDetalhes.id}`);
      setEmpresaDetalhes(res.data);
    } catch { toast.error('Erro ao recarregar detalhes'); }
  };

  const abrirEditar = async (empresa) => {
    try {
      const res = await api.get(`/superadmin/companies/${empresa.id}`);
      setEmpresaEditar(res.data);
      setShowEditar(true);
    } catch { toast.error('Erro ao carregar empresa'); }
  };

  const confirmarStatus = (empresa, novoStatus) => {
    setAcaoStatus({ empresa, novoStatus });
    setShowConfirmar(true);
  };

  const executarMudancaStatus = async () => {
    try {
      setLoadingStatus(true);
      await api.patch(`/superadmin/companies/${acaoStatus.empresa.id}/status`, {
        status: acaoStatus.novoStatus,
      });
      toast.success('Status atualizado!');
      setShowConfirmar(false);
      fetchEmpresas();
    } catch {
      toast.error('Erro ao atualizar status');
    } finally {
      setLoadingStatus(false);
    }
  };

  const STATUS_OPTIONS = [
    { value: 'ALL',       label: 'Todas'      },
    { value: 'ACTIVE',    label: 'Ativas'     },
    { value: 'SUSPENDED', label: 'Suspensas'  },
    { value: 'CANCELED',  label: 'Canceladas' },
  ];

  return (
    <div className="page-content">

      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-0 fw-bold">
            <FaBuilding className="me-2 text-primary" />Empresas
          </h4>
          <small className="text-muted">
            {meta.total} empresa{meta.total !== 1 ? 's' : ''} cadastrada{meta.total !== 1 ? 's' : ''}
          </small>
        </div>
        <Button variant="primary" onClick={() => setShowCriar(true)}>
          <FaPlus className="me-2" />Nova Empresa
        </Button>
      </div>

      <div className="card mb-3">
        <div className="card-body py-2">
          <div className="d-flex flex-wrap gap-3 align-items-center">
            <div className="input-group" style={{ maxWidth: '320px' }}>
              <span className="input-group-text">
                <FaSearch size={12} className="text-muted" />
              </span>
              <Form.Control size="sm" placeholder="Buscar por nome, e-mail ou CNPJ..."
                value={searchInput} onChange={e => setSearchInput(e.target.value)} />
            </div>
            <ButtonGroup size="sm">
              {STATUS_OPTIONS.map(opt => (
                <Button key={opt.value}
                  variant={filtroStatus === opt.value ? 'primary' : 'outline-primary'}
                  onClick={() => { setFiltroStatus(opt.value); setPage(1); }}>
                  {opt.label}
                </Button>
              ))}
            </ButtonGroup>
            <Button variant="outline-secondary" size="sm" onClick={fetchEmpresas}
              disabled={loading} className="ms-auto">
              <FaSync className={`me-1 ${loading ? 'fa-spin' : ''}`} />Atualizar
            </Button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body p-0">
          <div className="table-responsive">
            <Table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th>Empresa</th>
                  <th>CNPJ</th>
                  <th>Plano</th>
                  <th className="text-center">Usuários</th>
                  <th>Status</th>
                  <th>Cadastro</th>
                  <th className="text-center">Ações</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [1, 2, 3, 4, 5].map(i => <SkeletonRow key={i} />)
                ) : empresas.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-5 text-muted">
                      <FaBuilding size={32} className="mb-2 d-block mx-auto opacity-25" />
                      Nenhuma empresa encontrada
                    </td>
                  </tr>
                ) : (
                  empresas.map(empresa => {
                    const sub = empresa.subscriptions?.[0];
                    return (
                      <tr key={empresa.id}>
                        <td>
                          <div className="fw-semibold">{empresa.name}</div>
                          <small className="text-muted">{empresa.email}</small>
                        </td>
                        <td><small className="text-muted">{empresa.cnpj || '—'}</small></td>
                        <td><PlanBadge tier={sub?.plan?.tier} /></td>
                        <td className="text-center">
                          <span style={{ display: 'inline-block', backgroundColor: '#f8f9fa', color: '#212529', padding: '4px 8px', borderRadius: '8px', fontSize: '12px', fontWeight: 600, border: '1px solid #dee2e6' }}>
                            {empresa._count?.users ?? 0}
                          </span>
                        </td>
                        <td><StatusBadge status={empresa.status} /></td>
                        <td>
                          <small className="text-muted">
                            {new Date(empresa.createdAt).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}
                          </small>
                        </td>
                        <td>
                          <div className="d-flex gap-1 justify-content-center">
                            <Button variant="outline-primary"   size="sm" title="Ver detalhes"  onClick={() => abrirDetalhes(empresa)}><FaEye /></Button>
                            <Button variant="outline-secondary" size="sm" title="Editar empresa" onClick={() => abrirEditar(empresa)}><FaEdit /></Button>
                            {empresa.status !== 'ACTIVE'    && <Button variant="outline-success" size="sm" title="Ativar"    onClick={() => confirmarStatus(empresa, 'ACTIVE')}   ><FaCheckCircle /></Button>}
                            {empresa.status !== 'SUSPENDED' && <Button variant="outline-warning" size="sm" title="Suspender" onClick={() => confirmarStatus(empresa, 'SUSPENDED')}><FaBan /></Button>}
                            {empresa.status !== 'CANCELED'  && <Button variant="outline-danger"  size="sm" title="Cancelar"  onClick={() => confirmarStatus(empresa, 'CANCELED')} ><FaTimesCircle /></Button>}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        </div>

        {meta.totalPages > 1 && (
          <div className="card-footer d-flex justify-content-between align-items-center py-2">
            <small className="text-muted">
              Página {meta.page} de {meta.totalPages} — {meta.total} registros
            </small>
            <ButtonGroup size="sm">
              <Button variant="outline-secondary" disabled={page === 1 || loading} onClick={() => setPage(p => p - 1)}>← Anterior</Button>
              <Button variant="outline-secondary" disabled={page === meta.totalPages || loading} onClick={() => setPage(p => p + 1)}>Próxima →</Button>
            </ButtonGroup>
          </div>
        )}
      </div>

      <ModalCriarEmpresa  show={showCriar}    onClose={() => setShowCriar(false)}    onSuccess={fetchEmpresas} />
      <ModalEditarEmpresa show={showEditar}   onClose={() => setShowEditar(false)}   onSuccess={fetchEmpresas} empresa={empresaEditar} />
      <ModalDetalhes      show={showDetalhes} onClose={() => setShowDetalhes(false)} empresa={empresaDetalhes} onUsuarioToggle={recarregarDetalhes} />
      <ModalConfirmarStatus
        show={showConfirmar} onClose={() => setShowConfirmar(false)}
        empresa={acaoStatus.empresa} novoStatus={acaoStatus.novoStatus}
        onConfirmar={executarMudancaStatus} loading={loadingStatus}
      />
    </div>
  );
};

export default Empresas;
